/* eslint-disable react/no-unescaped-entities */
import { useRef, useState } from 'react';
import { api } from '@/lib/api';
import { Upload, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export function resolveImageUrl(url) {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `${BACKEND_URL}${url}`;
}

/**
 * Drag-and-drop image upload with preview.
 *  - `value`: current image URL (may be absolute or "/api/uploads/xxx.png")
 *  - `onChange(url)`: called with the new URL after successful upload, or '' on clear
 *  - `label`: optional small text shown above the dropzone
 *  - `aspectClass`: tailwind classes controlling the aspect ratio of the preview
 */
export default function ImageUpload({ value, onChange, label, aspectClass = 'aspect-video', testId = 'image-upload' }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image trop volumineuse (max 2 Mo)');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post('/uploads', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      onChange(r.data.url);
      toast.success('Image téléversée');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur d\'upload');
    } finally {
      setUploading(false);
    }
  };

  const previewUrl = resolveImageUrl(value);

  return (
    <div className="space-y-2">
      {label && <div className="text-xs text-zinc-500 uppercase tracking-wide font-medium">{label}</div>}
      <div
        data-testid={testId}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          const f = e.dataTransfer.files?.[0];
          if (f) upload(f);
        }}
        onClick={() => !uploading && !previewUrl && inputRef.current?.click()}
        className={`relative ${aspectClass} border-2 border-dashed rounded-md overflow-hidden transition cursor-pointer flex items-center justify-center
          ${dragging ? 'border-zinc-950 bg-zinc-50' : 'border-zinc-200 hover:border-zinc-400 bg-white'}`}
      >
        <input
          ref={inputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/gif"
          onChange={(e) => upload(e.target.files?.[0])}
          className="hidden"
          data-testid={`${testId}-input`}
        />
        {uploading ? (
          <div className="flex items-center gap-2 text-zinc-500 text-sm">
            <Loader2 className="h-4 w-4 animate-spin" /> Upload…
          </div>
        ) : previewUrl ? (
          <>
            <img src={previewUrl} alt="" className="w-full h-full object-contain" />
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onChange(''); }}
              data-testid={`${testId}-clear`}
              className="absolute top-2 right-2 w-7 h-7 rounded-full bg-white/90 border border-zinc-200 hover:bg-red-50 hover:border-red-300 flex items-center justify-center"
            >
              <X className="h-3.5 w-3.5 text-zinc-700" />
            </button>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); inputRef.current?.click(); }}
              className="absolute bottom-2 right-2 px-2.5 py-1 text-xs rounded-md bg-white/90 border border-zinc-200 hover:bg-zinc-100 font-medium"
            >
              Remplacer
            </button>
          </>
        ) : (
          <div className="flex flex-col items-center gap-2 text-zinc-400 text-xs text-center px-4">
            <Upload className="h-5 w-5" />
            <span>Glisser une image ou cliquer</span>
            <span className="text-[10px] text-zinc-400">PNG · JPG · WEBP · max 2 Mo</span>
          </div>
        )}
      </div>
    </div>
  );
}
